import { FC, memo, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button, Drawer, Link, useClipboard } from '@robocorp/components';
import { styled } from '@robocorp/theme';

import { IconCheck2, IconClose, IconCopy } from '@robocorp/icons/iconic';
import { useNavigate } from 'react-router-dom';
import { useParams } from '~/hooks';
import { useWorkItemQuery } from '~/queries';
import { WorkItemAvatar } from '~/components';
import { withErrorBoundary } from '~/hocs';
import { WorkItemLogArtifact } from '~/routes/workspace/processes/views/workItem/components/LogArtifact';
import { WorkItemAdvancedDetails } from './AdvancedDetails';
import { WorkItemContent } from './Content';
import { WorkItemLog } from './EventLog';

const DRAWER_WIDTH = 480;
const DRAWER_WIDTH_WIDE = 1342;

const ButtonClose = styled(Button)`
  position: absolute;
  top: ${({ theme }) => theme.space.$20};
  right: ${({ theme }) => theme.space.$20};

  ${({ theme }) => theme.screen.m} {
    top: ${({ theme }) => theme.space.$8};
    right: ${({ theme }) => theme.space.$8};
  }
`;

const StyledDrawer = styled(Drawer)<{ isWide: boolean }>`
  max-width: 100%;
  ${({ isWide, theme: { transition } }) =>
    isWide && `transition: width ${transition.duration.fast}ms ${transition.function.default};`}
  > button {
    display: none;
  }
`;

const Container = styled(Box)`
  height: 100vh;
  overflow: hidden;
  position: relative;
`;

const ContentContainer = styled(Box)`
  position: relative;
`;

export type LogType = 'audit' | 'file';

interface Props {
  onClose: () => void;
}

interface DetailsProps extends Required<Props> {
  logType: LogType | false;
  onLogToggle: (type: LogType) => void;
}

const BaseDetailsContent: FC<DetailsProps> = ({ logType, onLogToggle, onClose }) => {
  const { t } = useTranslation();
  const { onCopyToClipboard, copiedToClipboard } = useClipboard();
  const { workspaceShortName, organizationShortName, processId, workItemId } = useParams<
    'organizationShortName' | 'workspaceShortName' | 'processId' | 'workItemId'
  >();

  const { data: workItem } = useWorkItemQuery(
    { processId, workItemId },
    { refetchOnWindowFocus: true, refetchOnMount: true },
  );

  return (
    <Container display="flex" flexDirection="row-reverse" flex={1} minHeight="100%" width="100%">
      <ContentContainer
        display="flex"
        flexDirection="column"
        minWidth={logType !== false ? DRAWER_WIDTH : '100%'}
        flex={0}
      >
        <ButtonClose variant="ghost" aria-label="Close" icon={IconClose} onClick={onClose} />
        <Drawer.Header>
          {workItem ? (
            <Drawer.Header.Title title={t('workItem.singular')}>
              <WorkItemAvatar state={workItem.state} />
            </Drawer.Header.Title>
          ) : (
            <Drawer.Header.Title title={t('workItem.singular')} />
          )}
          <Drawer.Header.Description>
            <Box display="flex" gap="$4">
              <Link href={window?.location.href} color="content.subtle.light" target="_blank">
                {workItemId}
              </Link>
              <Button
                variant="ghost"
                icon={copiedToClipboard ? IconCheck2 : IconCopy}
                onClick={onCopyToClipboard(workItemId)}
                aria-label={t('copyToClipboard') as string}
              />
            </Box>
          </Drawer.Header.Description>
        </Drawer.Header>

        <WorkItemContent workItemId={workItemId} processId={processId} onClose={onClose}>
          {workItem && (
            <WorkItemAdvancedDetails
              organizationShortName={organizationShortName}
              workspaceShortName={workspaceShortName}
              workItem={workItem}
              logType={logType}
              onLogToggle={onLogToggle}
            />
          )}
        </WorkItemContent>
      </ContentContainer>

      {logType === 'audit' && (
        <WorkItemLog
          workItemId={workItemId}
          processId={processId}
          processRunId={workItem?.processRunId}
          stepId={workItem?.activityId}
          stepRunId={workItem?.activityRunId}
        />
      )}

      {logType === 'file' && !!workItem?.activityRunId && (
        <Box flex="1" backgrounds={['transparent', 'content.inversed']}>
          <WorkItemLogArtifact
            processId={processId}
            processRunId={workItem.processRunId}
            activityRunId={workItem.activityRunId}
          />
        </Box>
      )}
    </Container>
  );
};

const DetailsContent = withErrorBoundary(memo(BaseDetailsContent));

export const DataExplorerWorkItemDetails: FC = () => {
  const navigate = useNavigate();
  const { workItemId } = useParams<'workItemId'>();

  const [logType, setLogType] = useState<LogType | false>(false);
  const onLogTypeToggle = useCallback(
    (type: LogType) =>
      setLogType((currentType) => {
        if (currentType === false) return type;
        if (currentType === type) return false;
        return type;
      }),
    [],
  );

  const onClose = useCallback(
    () => navigate({ pathname: '..', search: window.location.search }, { replace: true }),
    [navigate],
  );

  useEffect(() => {
    setLogType(false);
  }, []);

  const isLogOpen = logType !== false;
  const width = isLogOpen ? DRAWER_WIDTH_WIDE : DRAWER_WIDTH;

  return (
    <StyledDrawer
      id="work-item-drawer"
      open
      onClose={onClose}
      width={width}
      isWide={isLogOpen}
      passive
    >
      <DetailsContent
        logType={logType}
        onLogToggle={onLogTypeToggle}
        onClose={onClose}
        key={workItemId}
      />
    </StyledDrawer>
  );
};
